import { createSlice } from "@reduxjs/toolkit";

import { upCase } from "@utility/utilityFunctions";

const initialState = {
  budgetGroupIds: [],
  budget: null,
  budgetIsActive: null,
  budgetType: null,
  channelId: null,
  channelIds: [],
  currentTerritoryId: null,
  dateRangeStart: null,
  dateRangeEnd: null,
  description: null,
  favoriteItems: [],
  groupIds: [],
  groupedBy: null,
  groupOrderHistoryBy: null,
  isActive: null,
  isOrderable: null,
  isVisible: null,
  name: null,
  orderId: null,
  orderType: null,
  orderWindowId: null,
  orderWindowIds: [],
  programId: null,
  programIds: [],
  promotionIds: [],
  promotionRedemptionType: null,
  promotionStatus: null,
  promotionType: null,
  purchaserIds: [],
  reportType: null,
  sku: null,
  inStock: null,
  stateIds: [],
  status: null,
  supplierIds: [],
  territoryIds: [],
  useOrderWindowTerritoryId: false,
  userIds: [],
  warehouse: null,
  sortBy: null,
  sortDirection: null,
  ignoreUserTerritory: false,
  ignoreUserChannel: false,
  reviewType: null,
  chipList: [],
  defaultFilters: null,
  clearFilters: false,
  clearName: false,
  clearOrderId: false,
  clearSku: false,
  sorted: false,
  filterType: null,
  brandIds: [],
};

const arrayFilters = [
  "budgetGroupIds",
  "groupIds",
  "territoryIds",
  "channelIds",
  "orderWindowIds",
  "programIds",
  "promotionIds",
  "purchaserIds",
  "supplierIds",
  "userIds",
  "stateIds",
  "brandIds",
];

const freeTypeFilters = ["name", "orderId", "sku", "search"];

// Filters can be set under keys that aren't declared in initialState (e.g. itemVisibilityCase,
// dateRange, search), so every key is removed rather than resetting a hand-maintained list.
const reset = (state, retainType) => {
  const { filterType, defaultFilters } = state;
  for (const key of Object.keys(state)) {
    delete state[key];
  }
  Object.assign(state, initialState);
  if (retainType) {
    state.filterType = filterType;
    state.defaultFilters = defaultFilters;
  }
};

const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setFilterType(state, action) {
      const { type } = action.payload;
      state.filterType = type;
    },
    setInitialFilters(state, action) {
      const { type, defaultFilters, paramFilters } = action.payload;
      reset(state, false);
      state.defaultFilters = defaultFilters;
      state.filterType = type;
      for (const filter in defaultFilters) {
        state[filter] = defaultFilters[filter];
      }
      if (paramFilters) {
        for (const filter in paramFilters) {
          state[filter] = paramFilters[filter];
        }
      }
      if (!type.includes("reports-")) {
        state.sorted = true;
      }
    },
    setDefaultFilters(state, action) {
      const { filterObject } = action.payload;
      reset(state, true);
      state.defaultFilters = { ...filterObject };
    },
    updateMultipleFilters(state, action) {
      const { filterObject } = action.payload;
      for (const filter in filterObject) {
        state[filter] =
          arrayFilters.includes(filter) && filter !== "groupIds"
            ? filterObject[filter].map((f) => ({
                name: f.code || f.name,
                id: f.id,
              }))
            : filterObject[filter];
      }
    },
    updateSingleFilter(state, action) {
      const { filter, value } = action.payload;
      state[filter] =
        arrayFilters.includes(filter) && filter !== "groupIds"
          ? value.map((f) => ({
              name: f.code || f.name || f.description,
              id: f.id,
            }))
          : value;
    },
    setClearFilter(state, action) {
      const { key, value } = action.payload;
      const clearMap = {
        name: "clearName",
        orderId: "clearOrderId",
        sku: "clearSku",
      };
      state[clearMap[key]] = value;
    },
    setClear(state) {
      state.clearFilters = true;
    },
    setSorted(state, action) {
      const { sorted } = action.payload;
      state.sorted = sorted;
    },
    setClearSku(state, action) {
      const { key, value } = action.payload;
      state[`${key}`] = value;
    },
    resetFilters(state) {
      reset(state, true);
    },
    clearFilterState(state) {
      reset(state, false);
    },
    setChips(state, action) {
      const { filterType } = action.payload;

      let chippable = [];
      if (filterType === "order-history") {
        chippable = [
          "groupIds",
          "orderType",
          "territoryIds",
          "programIds",
          "stateIds",
          "userIds",
          "orderId",
          "sku",
          "status",
        ];
      }
      if (filterType && filterType.includes("reports-")) {
        chippable = [
          "groupIds",
          "orderWindowIds",
          "programIds",
          "promotionIds",
          "supplierIds",
          "purchaserIds",
          "sku",
          "status",
          "orderType",
          "userIds",
          "territoryIds",
          "budget",
          "budgetGroupIds",
          "channelIds",
          "brandIds",
        ];
      }
      if (filterType && filterType === "promotion") {
        chippable = [
          "userIds",
          "promotionType",
          "promotionStatus",
          "promotionRedemptionType",
        ];
      }

      const filters = [];
      const stateObject = { ...state };

      const pushFilter = (filter) => {
        const splitter = stateObject[filter].includes("-") ? "-" : " ";
        const value = freeTypeFilters.includes(filter)
          ? stateObject[filter]
          : upCase(stateObject[filter], splitter);
        filters.push({ type: filter, value: value });
      };

      for (const filter in stateObject) {
        if (chippable.includes(filter)) {
          if (arrayFilters.includes(filter)) {
            stateObject[filter].forEach((f) => {
              if (typeof f === "object") {
                filters.push({ type: filter, value: f.name, id: f.id });
              }
            });
          } else if (filter === "favoriteItems") {
            if (stateObject[filter].length > 0) {
              filters.push({ type: filter, value: "Favorite Items" });
            }
          } else if (filter === "status") {
            if (
              !filterType.includes("reports-") &&
              stateObject[filter] &&
              stateObject[filter].split(",").length === 1
            ) {
              pushFilter(filter);
            }
            if (filterType.includes("reports-") && stateObject[filter]) {
              const value = stateObject[filter]
                .split(",")
                .map((f) => upCase(f, "-"))
                .join(", ");
              filters.push({ type: filter, value: value });
            }
          } else if (filter === "budget") {
            if (stateObject[filter]) {
              const value = stateObject[filter].name;
              filters.push({ type: filter, value: value });
            }
          } else if (stateObject[filter]) {
            pushFilter(filter);
          }
        }
      }
      state.chipList = filters;
    },
  },
});

export const {
  setFilterType,
  setInitialFilters,
  setDefaultFilters,
  updateMultipleFilters,
  updateSingleFilter,
  setSorted,
  setClear,
  setClearFilter,
  resetFilters,
  clearFilterState,
  setChips,
} = filterSlice.actions;

export default filterSlice.reducer;
